import React, { useEffect, useState } from "react";
import { Info } from "lucide-react";
import { Link } from "react-router-dom";
import CopyableContainer from "./CopyableContainer";
import LeagueTabHeading from "./LeagueTabHeading";
import Tooltip from "./Tooltip";
import { safeFetch } from "../utils/api";
import "./styles/tableStyles.css";

const BEST_TRIO_TOOLTIP =
    "Best Trio: The combined fantasy points from a team's highest-scoring QB, highest-scoring RB, and highest-scoring WR or TE that week.";

const RecordLabel = ({ record }) => {
    if (record.key !== "best_trio") return record.label;
    return (
        <span className="inline-flex items-center gap-1.5">
            <span>{record.label}</span>
            <Tooltip label={BEST_TRIO_TOOLTIP}>
                <button
                    type="button"
                    aria-label="About Best Trio"
                    className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-text-muted transition-colors hover:text-forest focus:outline-none focus:ring-2 focus:ring-mustard/70"
                >
                    <Info size={15} strokeWidth={2.5} aria-hidden="true" />
                </button>
            </Tooltip>
        </span>
    );
};

const formatValue = (record, raw = record.value) => {
    const value = record.units === "%" ? Number(raw * 100) : Number(raw);
    const digits = record.units === "%" ? 1 : 2;
    return `${value.toLocaleString(undefined, { maximumFractionDigits: digits })}${record.units ? ` ${record.units}` : ""}`;
};

const previousRecordEntries = (record, personal) => {
    if (personal && record.previous_records?.length) {
        const distinctResults = new Set(
            record.previous_records.map((previous) => `${previous.year}-${previous.week}-${previous.value}`)
        );
        if (distinctResults.size === 1) {
            return [{ ...record.previous_records[0], owner: null }];
        }
        return record.previous_records;
    }
    if (record.previous_value === null || record.previous_value === undefined) return [];
    return [{
        owner: record.previous_owner,
        year: record.previous_year,
        week: record.previous_week,
        value: record.previous_value,
    }];
};

const RecordTable = ({ title, records = [], week, personal, minimumGames = 8, loading }) => (
    <CopyableContainer
        title={<LeagueTabHeading title={title} />}
        fileName={title.toLowerCase().replace(/\s+/g, "-")}
        className="weekly-records-copyable overflow-hidden rounded-xl"
    >
        <div className="px-4 pb-4 sm:px-5 sm:pb-5"><div className="table-rounded-shell">
            <table className="table">
                <thead><tr><th>Scope</th><th>Record</th><th className="records-left-column">Holder</th><th className="records-left-column">Week</th><th className="records-left-column">Result</th></tr></thead>
                <tbody>
                    {loading ? <tr><td colSpan="5" className="text-center">Loading records...</td></tr> : null}
                    {!loading && records.length === 0 ? <tr><td colSpan="5" className="text-center">No {title.toLowerCase()} were broken in Week {week}.</td></tr> : null}
                    {!loading && [...records].sort((a, b) => a.direction.localeCompare(b.direction) || a.owner.localeCompare(b.owner)).map((record, index) => {
                        const previousEntries = previousRecordEntries(record, personal);
                        const showOwnerLabels = personal && previousEntries.length > 1;
                        return (
                            <tr key={`${record.scope}-${record.key}-${record.team_id || record.owner_key || index}`} className={index % 2 ? "odd-row" : "even-row"}>
                                <td><span className={`rounded px-2 py-1 text-[10px] font-black uppercase tracking-wide ${record.direction === "best" ? "bg-green-light text-green-800" : "bg-coral-light text-coral"}`}>{record.direction}</span></td>
                                <td className="font-bold"><RecordLabel record={record} /></td>
                                <td className="records-left-column font-semibold">{record.holder_display || record.owner}{!personal && record.previous_owner ? <span className="block text-xs font-medium text-text-muted">Previous: {record.previous_owner}</span> : null}</td>
                                <td className="records-left-column">Week {record.week}, {record.year}{previousEntries.map((previous) => <span key={`${previous.owner || "shared"}-${previous.year}-${previous.week}`} className="block text-xs font-medium text-text-muted">Previous{showOwnerLabels ? ` (${previous.owner})` : ""}: Week {previous.week}, {previous.year}</span>)}</td>
                                <td className="records-left-column font-mono font-bold text-forest">{formatValue(record)}{previousEntries.map((previous) => <span key={`${previous.owner || "shared"}-${previous.year}-${previous.week}-${previous.value}`} className="block text-xs font-sans font-medium text-text-muted">Previous{showOwnerLabels ? ` (${previous.owner})` : ""}: {formatValue(record, previous.value)}</span>)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div></div>
        {personal ? <p className="px-4 pb-4 text-xs font-medium text-text-muted sm:px-5 sm:pb-5">Personal Records require a minimum of {minimumGames} prior games. New co-owners are included when a shared record-breaking performance is also their new personal record.</p> : null}
    </CopyableContainer>
);

const SignalsTable = ({ signals = [], loading }) => (
    <CopyableContainer title={<LeagueTabHeading title="Other Signals" />} fileName="weekly-other-signals" className="weekly-records-copyable overflow-hidden rounded-xl">
        <div className="px-4 pb-4 sm:px-5 sm:pb-5"><div className="table-rounded-shell">
            <table className="table"><thead><tr><th>Moment</th><th>Owner</th><th>Result</th><th>History</th></tr></thead><tbody>
                {loading ? <tr><td colSpan="4" className="text-center">Loading signals...</td></tr> : null}
                {!loading && signals.length === 0 ? <tr><td colSpan="4" className="text-center">No other notable signals this week.</td></tr> : null}
                {!loading && signals.map((signal, index) => {
                    const ownerRanks = signal.owner_ranks || [];
                    const distinctRanks = new Set(ownerRanks.map((owner) => owner.personal_rank));
                    return <tr key={`${signal.key}-${signal.team_id || signal.owner}`} className={index % 2 ? "odd-row" : "even-row"}>
                        <td><span className="inline-flex rounded-full border border-mustard/50 bg-mustard-light px-2.5 py-1 text-xs font-black leading-tight text-forest">{signal.label}</span></td><td>{signal.holder_display || signal.owner}</td><td className="font-mono font-bold text-forest">{formatValue(signal)}</td><td>{ownerRanks.length > 1 && distinctRanks.size > 1 ? ownerRanks.map((owner) => <span key={owner.owner_key} className="block">{owner.owner_name}: {owner.personal_rank} in personal history</span>) : `${ownerRanks[0]?.personal_rank ?? signal.franchise_rank} in personal history`}<span className="block text-xs text-text-muted">{signal.league_rank} in league history</span></td>
                    </tr>;
                })}
            </tbody></table>
        </div></div>
    </CopyableContainer>
);

export default function WeeklyRecordsTab({ provider, leagueYear, leagueId, week }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [ownerScope, setOwnerScope] = useState("all");
    const [loadingRows, setLoadingRows] = useState(false);

    useEffect(() => {
        if (!week) return;
        setError(null);
        setLoadingRows(Boolean(data));
        safeFetch(`/api/weekly-records/${provider}/${leagueYear}/${leagueId}/${week}/?owner_scope=${ownerScope}`, {}, false, 2)
            .then(setData)
            .catch(() => setError("Weekly records could not be loaded."))
            .finally(() => setLoadingRows(false));
    }, [provider, leagueYear, leagueId, week, ownerScope]);

    if (error) return <p className="rounded border border-coral/30 bg-coral-light p-4 font-semibold text-coral">{error}</p>;
    if (!data) return <div className="rounded-xl border border-border bg-surface p-6 font-semibold text-text-muted">Loading weekly records...</div>;

    const coverageYears = data.coverage.years;
    const coverageRange = coverageYears.length
        ? `${Math.min(...coverageYears)}–${Math.max(...coverageYears)}`
        : "No completed seasons";

    return <div className="space-y-8">
        <div className="flex flex-wrap items-center gap-3">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 rounded-lg border border-mustard/50 bg-mustard-light px-4 py-3 text-[0.77rem] font-bold text-[#695515]">
                <span>History searched</span>
                <span>{coverageRange}</span>
                <span className="h-2 min-w-28 flex-1 overflow-hidden rounded bg-mustard/25"><span className="block h-full bg-mustard" style={{ width: `${data.coverage.percent}%` }} /></span>
                <span>{data.coverage.percent}% complete</span>
                <Link className="font-black text-forest underline" to={`/fantasy_stats/league-history/${provider}/${leagueYear}/${leagueId}/?tab=coverage`}>Fill gaps in Coverage →</Link>
            </div>
            <div className="flex rounded border border-border bg-white p-1 text-xs font-bold uppercase tracking-wide">
                <button type="button" onClick={() => setOwnerScope("current")} className={`rounded px-3 py-2 ${ownerScope === "current" ? "bg-forest text-white" : "text-text-muted"}`}>Current owners only</button>
                <button type="button" onClick={() => setOwnerScope("all")} className={`rounded px-3 py-2 ${ownerScope === "all" ? "bg-forest text-white" : "text-text-muted"}`}>All historical owners</button>
            </div>
        </div>
        <RecordTable title="League Records" records={data.league_records} week={week} loading={loadingRows} />
        <RecordTable title="Personal Records" records={data.franchise_records} week={week} personal minimumGames={data.personal_record_min_games || 8} loading={loadingRows} />
        <SignalsTable signals={data.other_signals} loading={loadingRows} />
    </div>;
}
